import { Box, Typography } from "@outschool/backpack";
import { getFundingProgramProperties } from "@outschool/funding-programs-shared";
import { Enrollment } from "@outschool/gql-backend-generated";
import { EnrollmentMeetingPurchaseQueryQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useFundingProgramAbbr, useSession } from "@outschool/ui-auth";
import React from "react";

import { ActivityEnrollQueryQuery } from "../../../../shared/generated/graphql";
import { isOrganizationAdmin } from "../../../lib/Users";
import LearnerItemCheckboxes, {
  LearnerItemType,
} from "./learnerItems/LearnerItemCheckboxes";
import LearnerItemDropdown from "./learnerItems/LearnerItemDropdown";

type OrderInputsProps = {
  activity:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
  learnerItems: LearnerItemType[];
  enrollmentsInOtherSections?: Enrollment[];
  buyingLearners?: LearnerItemType["learner"][];
  onLearnerItemsUpdate?(learners: LearnerItemType[]): void;
  requestAgeRangeException: (arg0: LearnerItemType) => void;
  disabled: boolean;
  highlightErrors: boolean;
  clearErrors?: () => void;
  onlyAllowSingleLearner?: boolean;
  ignoreStrictAgeRange: boolean;
  isThreeStepBooking?: boolean;
  isPrepayEnrollment?: boolean;
  usingEsaFunds?: boolean;
};

export default function OrderInputs({
  onLearnerItemsUpdate = () => {},
  learnerItems,
  disabled,
  highlightErrors,
  activity,
  ignoreStrictAgeRange,
  buyingLearners,
  requestAgeRangeException,
  clearErrors,
  onlyAllowSingleLearner = false,
  isThreeStepBooking = false,
  isPrepayEnrollment = false,
  usingEsaFunds = false,
}: OrderInputsProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\OrderInputs",
  );
  const esaState = useFundingProgramAbbr();
  const { currentUser, isEsaSession } = useSession();
  const fundingProgramProperties = getFundingProgramProperties(esaState);
  const isClassWalletProgram = fundingProgramProperties?.isClassWallet;

  return ((isThreeStepBooking && onlyAllowSingleLearner) || usingEsaFunds) &&
    !isOrganizationAdmin(currentUser) ? (
    <LearnerItemDropdown
      disabled={disabled}
      onLearnerItemsUpdate={onLearnerItemsUpdate}
      learnerItems={learnerItems}
      activity={activity}
      ignoreStrictAgeRange={ignoreStrictAgeRange}
      clearErrors={clearErrors}
      highlightErrors={highlightErrors}
    />
  ) : (
    <Box
      sx={{
        marginY: "1em",
        paddingBottom: "1em",
      }}
    >
      <Box flex sx={{ alignItems: "baseline" }}>
        <Typography
          variant="h6"
          sx={{
            my: "0.5em",
          }}
        >
          {t("Learners")}
        </Typography>
        {isEsaSession && isClassWalletProgram && (
          <>
            &nbsp;
            <Typography
              variant="body2"
              sx={{
                color: "grey.600",
                my: "0.5em",
              }}
            >
              {t`(ClassWallet funds are tied to a specific learner.)`}
            </Typography>
          </>
        )}
      </Box>
      <LearnerItemCheckboxes
        disabled={disabled && !isPrepayEnrollment}
        highlightErrors={highlightErrors}
        onLearnerItemsUpdate={onLearnerItemsUpdate}
        learnerItems={learnerItems}
        buyingLearners={buyingLearners}
        activity={activity}
        ignoreStrictAgeRange={ignoreStrictAgeRange}
        requestAgeRangeException={requestAgeRangeException}
        clearErrors={clearErrors}
        onlyAllowSingleLearner={onlyAllowSingleLearner || usingEsaFunds}
      />
    </Box>
  );
}
